<template>
  <button
    ref="buttonRef"
    :class="buttonClass"
    :disabled="disabled"
    :type="type"
    :aria-label="ariaLabel"
    :aria-describedby="ariaDescribedby"
    :aria-pressed="ariaPressed"
    :title="title"
    @click="handleClick"
    @keydown="handleKeyDown"
  >
    <span v-if="icon" class="btn__icon">
      <Icon :name="icon" :size="iconSize" :aria-hidden="!!$slots.default" />
    </span>
    <span v-if="$slots.default" class="btn__text" :class="{ 'ml-1': icon }">
      <slot />
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Icon from './Icon.vue';

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'icon';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  icon?: string;
  type?: 'button' | 'submit' | 'reset';
  // Accessibility props
  ariaLabel?: string;
  ariaDescribedby?: string;
  ariaPressed?: boolean | 'true' | 'false';
  title?: string;
  // Toggle behavior
  toggle?: boolean;
  pressed?: boolean;
}

const props = withDefaults(defineProps<ButtonProps>(), {
  variant: 'primary',
  size: 'medium',
  disabled: false,
  type: 'button',
  toggle: false,
  pressed: false
});

const emit = defineEmits<{
  click: [event: MouseEvent | KeyboardEvent];
  'update:pressed': [pressed: boolean];
}>();

const buttonRef = ref<HTMLButtonElement>();

// Computed properties
const buttonClass = computed(() => {
  return [
    'btn',
    `btn--${props.variant}`,
    `btn--${props.size}`,
    {
      'btn--disabled': props.disabled,
      'btn--pressed': props.pressed
    }
  ];
});

const iconSize = computed(() => {
  switch (props.size) {
    case 'small': return 12;
    case 'large': return 20;
    default: return 16;
  }
});

// Event handlers
const handleClick = (event: MouseEvent): void => {
  if (props.disabled) return;
  
  if (props.toggle) {
    emit('update:pressed', !props.pressed);
  }
  
  emit('click', event);
};

const handleKeyDown = (event: KeyboardEvent): void => {
  if (props.disabled) return;
  
  // Handle space and enter keys for button activation
  if (event.key === ' ' || event.key === 'Enter') {
    event.preventDefault();
    
    if (props.toggle) {
      emit('update:pressed', !props.pressed);
    }
    
    emit('click', event);
  }
};

// Expose button element for parent components
defineExpose({
  focus: () => buttonRef.value?.focus(),
  blur: () => buttonRef.value?.blur(),
  click: () => buttonRef.value?.click(),
  element: buttonRef
});
</script>

<style scoped lang="scss">
@use '@/styles/mixins';

.btn {
  @include button-style;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  border: none;
  cursor: pointer;
  font-family: var(--vscode-font-family);
  font-weight: var(--vscode-font-weight);
  line-height: 1;
  transition: background-color 0.1s ease, color 0.1s ease;
  
  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
  }
  
  &__text {
    display: inline-flex;
    align-items: center;
    line-height: 1.2;
    gap: 4px;
  }
  
  &--primary {
    background-color: var(--vscode-button-background);
    color: var(--vscode-button-foreground);
    
    &:hover:not(.btn--disabled) {
      background-color: var(--vscode-button-hoverBackground);
    }
  }
  
  &--secondary {
    background-color: transparent;
    color: var(--vscode-foreground);
    border: 1px solid var(--vscode-border);
    
    &:hover:not(.btn--disabled) {
      background-color: var(--vscode-list-hoverBackground);
    }
  }
  
  &--icon {
    background-color: transparent;
    color: var(--vscode-foreground);
    border: none;
    padding: var(--spacing-sm);
    border-radius: var(--border-radius-sm);
    min-width: unset;
    
    &:hover:not(.btn--disabled) {
      background-color: var(--vscode-list-hoverBackground);
    }
  }
  
  &--small {
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: 12px;
  }
  
  &--medium {
    padding: var(--spacing-sm) var(--spacing-lg);
    font-size: var(--vscode-font-size);
  }
  
  &--large {
    padding: var(--spacing-md) var(--spacing-xl);
    font-size: 14px;
  }
  
  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:focus {
    @include focus-outline;
  }
}

.ml-1 {
  margin-left: var(--spacing-sm);
}
</style>
